// Announcements, janaza alerts, notifications and the approvals inbox (pilot slice 10, ADR-0036).
import type { Role } from './auth';
import type { MessageStatus } from './messaging';

/** Who may write an announcement, approve someone else's, and send a janaza alert. */
export const ANNOUNCE_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY'];

export const ANNOUNCEMENT_KINDS = ['NOTICE', 'JANAZA'] as const;
export type AnnouncementKind = (typeof ANNOUNCEMENT_KINDS)[number];

export const AUDIENCES = ['ALL', 'WARDS', 'PRAVASI', 'HOUSE'] as const;
export type Audience = (typeof AUDIENCES)[number];

export const ANNOUNCEMENT_STATES = ['PENDING', 'SENT', 'REJECTED'] as const;
export type AnnouncementState = (typeof ANNOUNCEMENT_STATES)[number];

export const ANNOUNCE_LIMITS = { title: 80, body: 600, place: 60 } as const;

export interface AnnouncementInput {
  titleEn: string;
  titleMl?: string | null;
  bodyEn: string;
  bodyMl?: string | null;
  audience: Audience;
  wardCodes?: string[];
  houseId?: string | null;
}

export interface JanazaInput {
  deceasedMemberId: string;
  /** "2026-11-04T16:30" local (India) time. */
  prayerAt: string;
  prayerPlace: string;
  burialPlace: string;
  /** Default: the whole mahallu. */
  audience?: Exclude<Audience, 'HOUSE'>;
  wardCodes?: string[];
}

export interface Reach {
  /** Houses a message was queued for. */
  recipients: number;
  skippedConsent: number;
  skippedPhone: number;
  /** Where the queued messages are now. */
  byStatus: Partial<Record<MessageStatus, number>>;
}

export interface AnnouncementRow {
  id: string;
  kind: AnnouncementKind;
  titleEn: string;
  titleMl: string | null;
  bodyEn: string;
  bodyMl: string | null;
  audience: Audience;
  wards: string[];
  house: { id: string; houseNo: string; nameEn: string } | null;
  state: AnnouncementState;
  rejectReason: string | null;
  createdBy: string;
  approvedBy: string | null;
  createdAt: string;
  sentAt: string | null;
  reach: Reach;
  /** The signed-in person may approve or reject it (pending, and not their own). */
  canApprove: boolean;
  version: number;
}

export const NOTIFICATION_KINDS = ['CERT_TO_SIGN', 'ANNOUNCEMENT_TO_APPROVE', 'PRIVACY_REQUEST', 'JANAZA_SENT'] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface NotificationRow {
  id: string;
  kind: NotificationKind;
  params: Record<string, string>;
  link: string | null;
  createdAt: string;
  read: boolean;
}

export interface NotificationList {
  rows: NotificationRow[];
  unread: number;
}

/** What waits for the signed-in person, worked out live (GOV-05). */
export interface InboxItem {
  kind: 'CERT_SIGNATURE' | 'ANNOUNCEMENT_APPROVAL' | 'PRIVACY_REQUEST' | 'FAILED_MESSAGES';
  id: string | null;
  /** Short text: a person's name, a request number, a count. */
  label: string;
  since: string | null;
  link: string;
  overdue: boolean;
}
